/** Fertility demo LGBTQ+ page — localStorage keys and path/checklist model (no new CSS tokens). */

export const LS_LGBTQ_PATH = 'orivonai_lgbtq_path';
export const LS_LGBTQ_CHECKLIST = 'orivonai_lgbtq_checklist';
export const LG_BTQ_DEMO_UPDATE = 'orivonai-lgbtq-demo-update';

export const PATH_IDS = ['lesbian', 'gay', 'trans', 'solo'];

export const PATH_DISPLAY = {
  lesbian: { title: 'Lesbian couple', treatment: 'Reciprocal IVF' },
  gay: { title: 'Gay couple', treatment: 'Donor & carrier search' },
  trans: { title: 'Trans / non-binary', treatment: 'Egg freezing' },
  solo: { title: 'Solo journey', treatment: 'Donor selection' },
};

export const CHECKLIST_ITEMS = {
  lesbian: [
    { id: 'l1', label: 'Choose which partner provides eggs' },
    { id: 'l2', label: 'Choose which partner carries' },
    { id: 'l3', label: 'RE consultation complete' },
    { id: 'l4', label: 'Select sperm donor — anonymous or known' },
    { id: 'l5', label: 'Partner 1 — stim cycle underway' },
    { id: 'l6', label: 'Partner 2 — synchronized cycle planning' },
    { id: 'l7', label: 'Legal parental rights review' },
    { id: 'l8', label: 'Embryo transfer' },
    { id: 'l9', label: 'Two week wait' },
  ],
  gay: [
    { id: 'g1', label: 'Semen analysis — both partners' },
    { id: 'g2', label: 'Genetic carrier screening' },
    { id: 'g3', label: 'RE consultation complete' },
    { id: 'g4', label: 'Egg donor selected' },
    { id: 'g5', label: 'Gestational carrier search' },
    { id: 'g6', label: 'Legal pre-birth order' },
    { id: 'g7', label: 'IVF cycle with egg donor' },
    { id: 'g8', label: 'Embryo transfer to carrier' },
    { id: 'g9', label: 'Pregnancy confirmed' },
  ],
  trans: [
    { id: 't1', label: 'RE consultation about preservation' },
    { id: 't2', label: 'Egg or sperm freezing complete' },
    { id: 't3', label: 'Gender-affirming care timeline set' },
    { id: 't4', label: 'Embryo or gamete storage confirmed' },
    { id: 't5', label: 'Family building plan finalized' },
    { id: 't6', label: 'Legal parental rights review' },
    { id: 't7', label: 'Treatment cycle when ready' },
  ],
  solo: [
    { id: 's1', label: 'RE consultation complete' },
    { id: 's2', label: 'Diagnostic bloodwork done' },
    { id: 's3', label: 'Sperm or egg donor selected' },
    { id: 's4', label: 'Legal donor agreement' },
    { id: 's5', label: 'First IUI or IVF cycle' },
    { id: 's6', label: 'Two week wait' },
    { id: 's7', label: 'Result and next steps' },
  ],
};

/** Demo defaults when a path has no saved completions yet */
export const DEFAULT_COMPLETED = {
  lesbian: ['l1', 'l2', 'l3', 'l4'],
  gay: ['g1', 'g2', 'g3'],
  trans: ['t1'],
  solo: ['s1', 's2'],
};

export function notifyLgbtqDemoUpdate() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(LG_BTQ_DEMO_UPDATE));
}

export function loadLgbtqPath() {
  try {
    const p = localStorage.getItem(LS_LGBTQ_PATH);
    if (p && PATH_IDS.includes(p)) return p;
  } catch {
    /* ignore */
  }
  return null;
}

/** @returns {Record<string, string[]>} */
export function loadLgbtqChecklistByPath() {
  try {
    const raw = localStorage.getItem(LS_LGBTQ_CHECKLIST);
    if (!raw) return {};
    const o = JSON.parse(raw);
    if (o && typeof o === 'object' && !Array.isArray(o)) {
      const out = {};
      PATH_IDS.forEach((pid) => {
        if (Array.isArray(o[pid])) out[pid] = [...o[pid]];
      });
      return out;
    }
  } catch {
    /* ignore */
  }
  return {};
}

export function saveLgbtqPath(path) {
  if (!path || !PATH_IDS.includes(path)) return;
  try {
    localStorage.setItem(LS_LGBTQ_PATH, path);
  } catch {
    /* ignore */
  }
  notifyLgbtqDemoUpdate();
}

/** Clears the selected chapter (localStorage). Checklist data per path is kept. */
export function clearLgbtqPath() {
  try {
    localStorage.removeItem(LS_LGBTQ_PATH);
  } catch {
    /* ignore */
  }
  notifyLgbtqDemoUpdate();
}

export function saveLgbtqChecklistByPath(byPath) {
  try {
    localStorage.setItem(LS_LGBTQ_CHECKLIST, JSON.stringify(byPath));
  } catch {
    /* ignore */
  }
  notifyLgbtqDemoUpdate();
}

/** @param {string} path */
export function getCompletedIdsForPath(path, byPath) {
  const saved = byPath[path];
  if (saved && Array.isArray(saved) && saved.length > 0) return new Set(saved);
  return new Set(DEFAULT_COMPLETED[path] || []);
}

/**
 * @param {string} path
 * @param {Set<string>} done
 * @returns {{ currentId: string | null, ordered: { id: string, label: string, state: 'done'|'current'|'todo' }[] }}
 */
export function deriveChecklistRows(path, done) {
  const items = CHECKLIST_ITEMS[path] || [];
  const ordered = [];
  let seenCurrent = false;
  let currentId = null;
  for (const { id, label } of items) {
    let state;
    if (done.has(id)) {
      state = 'done';
    } else if (!seenCurrent) {
      state = 'current';
      seenCurrent = true;
      currentId = id;
    } else {
      state = 'todo';
    }
    ordered.push({ id, label, state });
  }
  return { currentId, ordered };
}

export function markNextChecklistComplete(path, byPath) {
  const done = getCompletedIdsForPath(path, byPath);
  const items = CHECKLIST_ITEMS[path] || [];
  const nextIncomplete = items.find(({ id }) => !done.has(id));
  if (!nextIncomplete) return byPath;
  const nextDone = new Set(done);
  nextDone.add(nextIncomplete.id);
  const nextByPath = { ...byPath, [path]: [...nextDone] };
  saveLgbtqChecklistByPath(nextByPath);
  return nextByPath;
}

export function novaBarMessage(path) {
  if (!path) {
    return 'Your identity and your path to parenthood both belong here — Nova can help you find community and language that fits.';
  }
  if (path === 'lesbian') {
    return "Partner 1 is on stim day 8. Partner 2 — Nova can help you start cycle tracking for your synchronized transfer. Ready when you are.";
  }
  if (path === 'gay') {
    return 'Nova can help you prepare questions for your egg donor consultation and gestational carrier search. Both processes running in parallel.';
  }
  if (path === 'trans') {
    return 'Your identity and your path to parenthood both belong here. Nova can help you think through fertility preservation timing before or during your transition.';
  }
  return 'Building a family on your own terms takes courage. Nova is here to help you think through every step — from donor selection to your first IUI or IVF cycle.';
}

export function novaNoteForCurrent(path, currentId) {
  if (path === 'lesbian' && currentId === 'l5') {
    return 'Next: Partner 2 cycle sync — Nova can help coordinate timing for the transfer.';
  }
  if (path === 'gay' && currentId === 'g4') {
    return 'Nova can help you prepare questions for your egg donor consultation.';
  }
  if (path === 'trans' && currentId === 't2') {
    return 'Nova can help you track your preservation timeline and prepare for your clinic.';
  }
  if (path === 'solo' && currentId === 's3') {
    return 'Nova can help you compare donor profiles and prepare questions for your RE.';
  }
  if (path === 'lesbian') return 'Nova can help coordinate reciprocal IVF timing and questions for your clinic.';
  if (path === 'gay') return 'Nova can help you prepare for donor and carrier milestones.';
  if (path === 'trans') return 'Nova can help you align preservation with your care plan.';
  return 'Nova can help you plan your next step with confidence.';
}

export function financialRows(path) {
  if (path === 'lesbian') {
    return {
      rows: [
        { left: 'Sperm donor', right: '$1,800', strong: true },
        { left: 'IVF cycle — Partner 1', right: '$18,000', strong: true },
        { left: 'Embryo transfer — Partner 2', right: '~$4,500', mutedPink: true },
        { left: 'Legal — parental rights', right: '~$2,500', mutedPink: true },
      ],
      totalLabel: 'Total tracked',
      totalValue: '$26,800',
    };
  }
  if (path === 'gay') {
    return {
      rows: [
        { left: 'Egg donor', right: '~$25,000–$40,000', mutedPink: true },
        { left: 'IVF cycle', right: '~$18,000', mutedPink: true },
        { left: 'Gestational carrier', right: '~$100,000–$150,000', mutedPink: true },
        { left: 'Legal — pre-birth order', right: '~$5,000', mutedPink: true },
      ],
      totalLabel: 'Estimated total',
      totalValue: '$148,000+',
    };
  }
  if (path === 'trans') {
    return {
      rows: [
        { left: 'Egg or sperm freezing', right: '~$6,000', mutedPink: true },
        { left: 'Annual storage', right: '~$600/yr', strong: true },
        { left: 'Future treatment (est.)', right: '~$15,000–$25,000', mutedPink: true },
        { left: 'Legal', right: '~$2,500', mutedPink: true },
      ],
      totalLabel: 'Estimated preservation cost',
      totalValue: '$6,600+',
    };
  }
  if (path === 'solo') {
    return {
      rows: [
        { left: 'Sperm or egg donor', right: '~$1,800–$5,000', mutedPink: true },
        { left: 'IUI cycle', right: '~$1,500–$4,000', mutedPink: true },
        { left: 'IVF if needed', right: '~$15,000–$20,000', mutedPink: true },
        { left: 'Legal donor agreement', right: '~$1,500', mutedPink: true },
      ],
      totalLabel: 'Starting cost (IUI)',
      totalValue: '~$3,300',
    };
  }
  return null;
}

export function communityRooms(path) {
  const map = {
    lesbian: [
      { name: 'Reciprocal IVF — stim week', status: '8 live', live: true },
      { name: 'Lesbian couples — cycle sync', status: '4 live', live: true },
      { name: 'LGBTQ+ legal Q&A', status: 'Wed 7pm', live: false },
    ],
    gay: [
      { name: 'Gay dads — egg donor process', status: '6 live', live: true },
      { name: 'Surrogate matching — questions', status: '3 live', live: true },
      { name: 'Two dads — TWW support', status: 'Tomorrow 8pm', live: false },
    ],
    trans: [
      { name: 'Trans fertility preservation', status: '5 live', live: true },
      { name: 'Non-binary family building', status: '3 live', live: true },
      { name: 'Fertility before transition — Q&A', status: 'Mon 7pm', live: false },
    ],
    solo: [
      { name: 'Solo parents — first IUI', status: '7 live', live: true },
      { name: 'Single parent IVF journey', status: '4 live', live: true },
      { name: 'Solo moms — donor selection', status: 'Fri 8pm', live: false },
    ],
  };
  return map[path] || [];
}

export function novaSidebarInclusive(path) {
  const map = {
    lesbian: {
      title: 'Reciprocal IVF — what to expect',
      body: 'Partner 1 retrieval is 6 days away. Partner 2 sync cycle starts now. Nova has your coordination checklist ready.',
      link: 'See checklist →',
    },
    gay: {
      title: 'Egg donor consultation prep',
      body: 'Nova has prepared 8 questions to bring to your egg donor consultation — covering genetics, availability, and legal rights.',
      link: 'See questions →',
    },
    trans: {
      title: 'Preservation timeline',
      body: 'Nova can help you think through the timing of egg or sperm freezing relative to your gender-affirming care schedule.',
      link: 'Open Nova →',
    },
    solo: {
      title: 'Donor selection guide',
      body: 'Nova can help you compare anonymous vs. known donors, understand sperm bank screening standards, and prepare for your first IUI.',
      link: 'Open Nova →',
    },
  };
  return (
    map[path] || {
      title: 'Inclusive support',
      body: 'Your identity and your path to parenthood both belong here — Nova can help you find community and language that fits.',
      link: 'Open Nova →',
    }
  );
}

export function articleTitleForPath(path) {
  if (path === 'gay') return 'Egg donation and surrogacy — step by step';
  if (path === 'trans') return 'Fertility preservation before gender-affirming care';
  if (path === 'solo') return 'Solo IVF — what to expect as a single parent';
  return 'What is reciprocal IVF — and is it right for you?';
}

export function communityMatch(path) {
  const map = {
    lesbian: { initials: 'JL', name: 'Jess L.', sub: 'Reciprocal IVF · stim week · Columbus, Ohio' },
    gay: { initials: 'MK', name: 'Mike K.', sub: 'Gay dad · egg donor search · cycle 1' },
    trans: { initials: 'AJ', name: 'Alex J.', sub: 'Trans · egg freezing · pre-transition' },
    solo: { initials: 'SR', name: 'Sara R.', sub: 'Solo mom · IUI cycle 2 · Columbus' },
  };
  return map[path] || { initials: 'JL', name: 'Jess L.', sub: 'Reciprocal IVF · stim week · Columbus, Ohio' };
}
